import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormGroupDirective, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
	CreateBankruptcyRequest,
	ManagementBankruptcyControllerService,
	ManagementBankruptcyResponse,
	ManagementBusinessEntityControllerService,
	ManagementBusinessEntityResponse,
} from 'api-contract-notices';
import { UtilsService } from '../../../../../shared/services/utils.service';
import { Subject } from 'rxjs/internal/Subject';
import { debounceTime, distinctUntilChanged, startWith, take, takeUntil } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { BusinessEntityComponent } from '../../../management/business-entity/business-entity';
import { UploadManagementService } from '../../../../../shared/services/upload-management.service';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SharedService } from '../../../../../shared/services/shared.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDatepickerModule } from '@angular/material/datepicker';

@Component({
	selector: 'app-bankruptcies-form',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatAutocompleteModule,
		MatButtonModule,
		MatIconModule,
		TranslateModule,
		MatTooltipModule,
		MatSelectModule,
		MatProgressSpinnerModule,
		MatDatepickerModule,
	],
	templateUrl: './bankruptcies-form.component.html',
	styleUrl: './bankruptcies-form.component.scss',
})
export class BankruptciesFormComponent implements OnInit, OnChanges, OnDestroy {
	onDestory: Subject<boolean> = new Subject();

	@Input() itemForEdit: ManagementBankruptcyResponse | undefined;
	@Output() actionRequested: EventEmitter<any> = new EventEmitter();

	bankruptciesFormGroup: FormGroup = new FormGroup({});

	mode: 'NEW' | 'EDIT' = 'NEW';
	private readonly DEBOUNCE_TIME = 450;

	apiLoading = false;

	filteredBusinessEntities: ManagementBusinessEntityResponse[] = [];

	phases: string[] = [
		'PRE_BANKRUPTCY',
		'BANKRUPTCY_PROCEEDINGS_OPEN',
		'CREDITORS_ASSEMBLY',
		'FILING_IN_BANKRUPTCY_FILE',
		'BANKRUPTCY_PROCEEDINGS_OPEN_CLOSE',
    	'BANKRUPTCY_PROCEEDINGS_OPEN_CREDITORS_ASSEMBLY',
		'BANKRUPTCY_PROCEEDINGS_CLOSE',
		'SUSPEND_PRE_BANKRUPTCY',
		'BANKRUPTCY_TRUSTEE_CHANGE',
		'PROPERTY_SALE',
		'PROPERTY_ASSESSMENT',
		'PROCUREMENT_SERVICES_GOODS',
		'OTHER',
	];

	constructor(
		private fb: FormBuilder,
		public utils: UtilsService,
		private apiManagementBusinessEntity: ManagementBusinessEntityControllerService,
		private dialog: MatDialog,
		private uploadManagement: UploadManagementService,
		private apiManagementBankruptcy: ManagementBankruptcyControllerService,
		private sharedService: SharedService,
		private translate: TranslateService
	) {
		this.sharedService.requestActionInComponent.pipe(takeUntilDestroyed()).subscribe({
			next: response => {
				if (response.componentName === 'BankruptciesFormComponent') {
					switch (response.action) {
						case 'RESET_FORM': {
							this.resetForm();
							break;
						}
					}
				}
			},
		});

		this.formInitializer();
	}

	ngOnInit(): void {
		this.formControlWatchers();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['itemForEdit'].currentValue) {
			this.bankruptciesFormGroup.reset();
			this.mode = 'EDIT';
			this.populateForm();
		}
	}

	ngOnDestroy(): void {
		this.onDestory.next(true);
	}

	//** Form methods */
	private formInitializer() {
		this.bankruptciesFormGroup = this.fb.group({
			company: ['', Validators.required],
			embs: [, Validators.required],
			fileUrl: [],
			phase: [, Validators.required],
			publicationDate: [new Date(), Validators.required],
			text: [],
		});
	}

	private formControlWatchers() {
		this.bankruptciesFormGroup.controls['company'].valueChanges
			.pipe(startWith(''), debounceTime(this.DEBOUNCE_TIME), distinctUntilChanged(), takeUntil(this.onDestory))
			.subscribe(searchTerm => {
				this.bankruptciesFormGroup.controls['embs'].setValue(searchTerm && searchTerm.embs && typeof searchTerm === 'object' ? searchTerm.embs : null);

				this.itemAction('SEARCH', 'COMPANY', searchTerm);
			});
	}

	checkFormValidity() {
		return this.bankruptciesFormGroup.controls['fileUrl'].value || this.bankruptciesFormGroup.controls['text'].value;
	}

	private populateForm() {
		if (this.itemForEdit) {
			this.bankruptciesFormGroup.patchValue(this.itemForEdit);
		}
	}

	resetForm(formDirective?: FormGroupDirective) {
		this.bankruptciesFormGroup.reset();
		this.filteredBusinessEntities = [];

		setTimeout(() => {
			this.bankruptciesFormGroup.controls['publicationDate'].setValue(new Date());
		}, 50);

		if (formDirective) {
			formDirective.resetForm();
		}

		this.mode = 'NEW';
	}
	//** (END) Form methods */

	itemAction(action: 'SEARCH' | 'CREATE' | 'SELECT', type: 'COMPANY', data: string | ManagementBusinessEntityResponse) {
		switch (type) {
			case 'COMPANY': {
				if (action === 'SEARCH') {
					this.searchBusinessEntities(data as string);
				} else if (action === 'CREATE') {
					this.openBusinessEntityDialog(data as ManagementBusinessEntityResponse);
				} else if (action === 'SELECT') {
					this.bankruptciesFormGroup.controls['embs'].setValue((data as ManagementBusinessEntityResponse).embs);
				}
				break;
			}
		}
	}

	//** Business entity methods */
	searchBusinessEntities(searchTerm: string) {
		if (searchTerm && searchTerm.length) {
			this.apiLoading = true;

			this.apiManagementBusinessEntity.getBusinessEntitiesPageable({businessEntity: searchTerm}, 0, 100)
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.filteredBusinessEntities = response.content || [];
						this.apiLoading = false;
					},
					error: () => {
						this.apiLoading = false;
					},
				});
		}
	}

	displayFnBusinessEntity(item: ManagementBusinessEntityResponse): string {
		return item && item.id ? this.utils.getParameterByLanguageScript(item, 'name', true) : '';
	}

	openBusinessEntityDialog(item?: ManagementBusinessEntityResponse) {
		this.filteredBusinessEntities = [];

		const dialogRef = this.dialog.open(BusinessEntityComponent, {
			width: '600px',
			data: {
				mode: item ? 'EDIT' : 'NEW',
				type: 'BUSINESS_ENTITY',
				item,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe((result: ManagementBusinessEntityResponse) => {
				if (result) {
					this.bankruptciesFormGroup.controls['company'].setValue(result);
					this.bankruptciesFormGroup.controls['embs'].setValue(result.embs);
				}
			});
	}

	//** (END) Business entity methods */

	//** Image upload methods */
	getAcceptedFormats() {
		return ['image/*', 'application/pdf'];
	}

	selectFilesForUpload(event: any) {
		this.uploadManagement.generatePresignedUrl(event.target.files[0], 'BANKRUPTCY', 'CONTRACT_NOTICES');
		this.uploadManagement.linkGenerated.pipe(take(1)).subscribe(response => {
			this.bankruptciesFormGroup.controls['fileUrl'].setValue(response);
		});
	}
	//** (END) Image upload methods */

	submitBankruptciesForm(formDirective: FormGroupDirective, event: Event) {
		event.stopPropagation();
		event.preventDefault();
		this.apiLoading = true;

		const bankruptcyRequest: CreateBankruptcyRequest = {
			companyId: this.bankruptciesFormGroup.controls['company'].value.id,
			fileUrl: this.bankruptciesFormGroup.controls['fileUrl'].value,
			phase: this.bankruptciesFormGroup.controls['phase'].value,
			text: this.bankruptciesFormGroup.controls['text'].value,
      		publicationDate: this.bankruptciesFormGroup.controls['publicationDate'].value,
		};

		if (this.mode === 'NEW') {
			this.apiManagementBankruptcy
				.createBankruptcy(bankruptcyRequest)
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
							type: 'SUCCESS',
						});

						if (bankruptcyRequest.fileUrl) {
							this.uploadManagement.uploadFile(response);
						}

						this.resetForm(formDirective);
						this.actionRequested.emit({ action: 'LOAD' });
						this.apiLoading = false;
					},
					error: err => {
						this.sharedService.showNotification.next({
							message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
							type: 'ERROR',
						});
						this.apiLoading = false;
					},
				});
		} else if (this.mode === 'EDIT' && this.itemForEdit) {
			this.apiManagementBankruptcy
				.updateBankruptcy(bankruptcyRequest, this.itemForEdit.id)
				.pipe(take(1))
				.subscribe({
					next: () => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
							type: 'SUCCESS',
						});

						if (this.itemForEdit && !this.utils.compareSuppressNulls(this.itemForEdit.fileUrl, bankruptcyRequest.fileUrl)) {
							this.uploadManagement.uploadFile(this.itemForEdit);
						}

						this.resetForm(formDirective);
						this.actionRequested.emit({ action: 'LOAD' });
						this.apiLoading = false;
						this.mode = 'NEW';
					},
					error: err => {
						this.sharedService.showNotification.next({
							message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
							type: 'ERROR',
						});
						this.apiLoading = false;
					},
				});
		}
	}
}
